import type { ShortVideo } from '~/composables/shorts/useFetchShorts';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useStaleGuard } from '~/utils';
import { defineStore } from 'pinia';

function uniqueShortVideosByCommercialId(items: ShortVideo[]): ShortVideo[] {
	const seenCommercialIds = new Set<string>();

	return items.filter(item => {
		const commercialId = item.property.commercialId;
		if (seenCommercialIds.has(commercialId)) return false;

		seenCommercialIds.add(commercialId);
		return true;
	});
}

export const useShortsFeedStore = defineStore('shortsFeed', () => {
	const videos = shallowRef<ShortVideo[]>([]);
	const isLoading = ref(false);
	const isPreloaded = ref(false);
	const hasMore = ref(true);
	const currentPage = ref(1);
	const activeFilters = shallowRef<ShortsFilters>({});
	const allWatched = ref(false);
	const totalAvailable = ref(0);

	const staleGuard = useStaleGuard();

	function setVideos(items: ShortVideo[]) {
		videos.value = uniqueShortVideosByCommercialId(items);
	}

	function appendVideos(items: ShortVideo[]) {
		videos.value = uniqueShortVideosByCommercialId([...videos.value, ...items]);
	}

	function reset() {
		videos.value = [];
		isLoading.value = false;
		isPreloaded.value = false;
		hasMore.value = true;
		currentPage.value = 1;
		activeFilters.value = {};
		allWatched.value = false;
		totalAvailable.value = 0;
		staleGuard.next();
	}

	return {
		videos,
		isLoading,
		isPreloaded,
		hasMore,
		currentPage,
		activeFilters,
		allWatched,
		totalAvailable,
		guardNext: staleGuard.next,
		guardIsStale: staleGuard.isStale,
		setVideos,
		appendVideos,
		reset
	};
});
